/**
 * Photographs of a scheme, and of the things inside it.
 *
 * **A scheme with no pictures has nothing to show.** The marketplace card is a photograph and a
 * price; the description is what somebody reads after the picture has made them stop. So an estate
 * gets a gallery, and so does every plot, house, warehouse or block inside it.
 *
 * **The first one is not merely first — it is the leading image.** It is the picture the public
 * card uses, the one at the top of the scheme's page, and the one a share preview shows. That
 * makes ordering an editorial decision rather than a preference, so it is made by dragging, which
 * is how everybody expects to make it, and the tile says out loud which one it is.
 *
 * This is `components/unit-photos.tsx` generalised: the same browser-native drag rather than a
 * forty-kilobyte dependency, the same two arrow buttons per tile because a drag is unreachable
 * without a mouse, and the same rule that the server is told the whole new order rather than a
 * pair of indexes — a reorder is one intention.
 *
 * **The pictures come back through an authorised endpoint, not the public one.** Most of what a
 * developer photographs is not on the market, and `/public/development-media/:id` refuses anything
 * that is not — correctly. The agency's own screens read `/development-media/:id`, which is scoped
 * to their session. Getting this the wrong way round produces a gallery of broken images on every
 * unlisted plot, which is most of them.
 */

import { useRef, useState, type SVGProps } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useApiMutation } from "@/lib/mutations";
import { Alert, Button } from "@/components/ui";
import { IconClose, IconDrag, IconPlus } from "@/components/icons";

type IconProps = SVGProps<SVGSVGElement> & { size?: number };

/**
 * Two marks drawn here rather than imported.
 *
 * Same construction as `components/icons.tsx` — 24 grid, 1.75 stroke, round caps and joins,
 * `currentColor` — so they sit with the rest of the set. They live beside the one component that
 * uses them, exactly as `agency-mark.tsx` keeps its own two.
 */
function Icon({ size = 18, children, ...rest }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...rest}
    >
      {children}
    </svg>
  );
}

/** A framed picture with a horizon and a sun — the universal shorthand for a photograph. */
export const IconPhoto = (props: IconProps) => (
  <Icon {...props}>
    <rect x="3" y="4.5" width="18" height="15" rx="2" />
    <circle cx="8.5" cy="9.5" r="1.5" />
    <path d="m3.5 17 4.5-4.5 3.5 3.5 3-3 6 6" />
  </Icon>
);

/**
 * A star, for the picture everything else leads with.
 *
 * Solid rather than outlined, because it marks a state rather than offering an action — the same
 * distinction the pin on a pinned row makes.
 */
export const IconLeading = (props: IconProps) => (
  <Icon {...props} fill="currentColor" strokeWidth={1.25}>
    <path d="m12 3.6 2.6 5.27 5.82.85-4.21 4.1.99 5.79L12 16.87l-5.2 2.74.99-5.79-4.21-4.1 5.82-.85L12 3.6Z" />
  </Icon>
);

export interface DevelopmentPhoto {
  id: string;
  name: string;
  width: number | null;
  height: number | null;
  position: number;
}

/** What kind of thing the gallery hangs off. It is fixed by the screen, never by the request. */
export type GalleryOwner = "estate" | "plot";

/**
 * Where a photograph is served from, for a screen inside the agency.
 *
 * Served by the API after it has looked the row up and checked the organisation — never a path
 * built here, and never the public endpoint, which would show nothing for anything not on the
 * market.
 */
export const developmentPhotoSrc = (id: string) => `/api/v1/development-media/${id}`;

/** And where a stranger's browser gets it. Refused unless the thing is genuinely on the market. */
export const publicPhotoSrc = (id: string) => `/api/v1/public/development-media/${id}`;

/** Twelve to a gallery, matching the server. Past that nobody scrolls. */
export const MAX_GALLERY = 12;

const basePath = (owner: GalleryOwner, id: string) => (owner === "estate" ? `/estates/${id}` : `/plots/${id}`);

/**
 * The upload itself, on its own so a caller with its own button can use it.
 *
 * `api.post` sends JSON; a photograph is multipart, and base64 in a JSON body would make a five
 * megabyte picture a third bigger on the connection that is already the slow part. Returns the
 * problem to show, or null when everything landed.
 */
export async function uploadDevelopmentPhotos(
  owner: GalleryOwner,
  ownerId: string,
  files: File[],
): Promise<string | null> {
  if (!files.length) return null;

  const form = new FormData();
  for (const file of files) form.append("photos", file);

  try {
    const response = await fetch(`/api/v1${basePath(owner, ownerId)}/photos`, {
      method: "POST",
      body: form,
      credentials: "include",
    });
    if (response.ok) return null;
    const payload = (await response.json().catch(() => null)) as { error?: string } | null;
    return payload?.error ?? "Those photographs did not upload.";
  } catch {
    return "Could not reach the server. Check your connection and try again.";
  }
}

/**
 * A read-only strip of thumbnails.
 *
 * For a row in a list, where the point is to recognise something rather than to edit it.
 */
export function PhotoThumbnail({
  photoId,
  alt,
  className = "size-12",
}: {
  photoId: string | null;
  alt: string;
  className?: string;
}) {
  if (!photoId) {
    /* Not an empty box and not a broken image: a deliberate placeholder, so a scheme with no
       photographs looks unfinished in a way somebody can act on rather than looking broken. */
    return (
      <span
        aria-hidden="true"
        className={`grid shrink-0 place-items-center rounded-[var(--radius-control)] bg-ink-100 text-ink-400 ${className}`}
      >
        <IconPhoto size={16} />
      </span>
    );
  }

  return (
    <img
      src={developmentPhotoSrc(photoId)}
      alt={alt}
      loading="lazy"
      className={`shrink-0 rounded-[var(--radius-control)] object-cover ${className}`}
    />
  );
}

/**
 * The editable gallery.
 *
 * `canEdit` decides whether anything can be added, dragged or removed. It is the caller's
 * permission check rather than this component's, because the two screens that use it guard on
 * different permissions — and a control that is present but refuses is worse than one that is
 * absent.
 */
export function PhotoGallery({
  owner,
  ownerId,
  canEdit = true,
  /** What one of these is called on this estate — "plot", "home", "unit". ADR-0081. */
  noun = "item",
}: {
  owner: GalleryOwner;
  ownerId: string;
  canEdit?: boolean;
  noun?: string;
}) {
  const client = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const key = ["development-photos", owner, ownerId];

  const { data, isLoading } = useQuery({
    queryKey: key,
    queryFn: () => api.get<{ photos: DevelopmentPhoto[] }>(`${basePath(owner, ownerId)}/photos`),
  });

  const photos = data?.photos ?? [];

  const reorder = useApiMutation({
    mutationFn: (ids: string[]) => api.patch(`${basePath(owner, ownerId)}/photos`, { ids }),
    invalidates: [key, ["estate"], ["estates"]],
  });

  const remove = useApiMutation({
    mutationFn: (id: string) => api.delete(`/development-photos/${id}`),
    invalidates: [key, ["estate"], ["estates"]],
  });

  async function upload(files: FileList | null) {
    if (!files?.length) return;
    setProblem(null);
    setUploading(true);

    const failed = await uploadDevelopmentPhotos(owner, ownerId, Array.from(files));
    if (failed) setProblem(failed);
    else {
      await client.invalidateQueries({ queryKey: key });
      // The estate screen shows the leading image on every row, so it has to be refreshed too.
      await client.invalidateQueries({ queryKey: ["estate"] });
    }

    setUploading(false);
    if (fileInput.current) fileInput.current.value = "";
  }

  /** Moves one photograph to where another one is, and tells the server the whole new order. */
  const moveTo = (id: string, index: number) => {
    const without = photos.filter((photo) => photo.id !== id).map((photo) => photo.id);
    without.splice(index, 0, id);
    reorder.mutate(without);
  };

  const move = (id: string, by: number) => {
    const from = photos.findIndex((photo) => photo.id === id);
    const to = Math.min(Math.max(from + by, 0), photos.length - 1);
    if (from !== to) moveTo(id, to);
  };

  return (
    <div>
      {problem ? (
        <div className="mb-3">
          <Alert tone="danger">{problem}</Alert>
        </div>
      ) : null}

      {isLoading ? (
        <p className="text-sm text-ink-500">Loading photographs…</p>
      ) : (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(10.5rem,1fr))] gap-3">
          {/* Ten and a half rem, not the seven and a half the unit form uses. This gallery sits in
              a full-width card rather than in a narrow column, and at 120px the "Leading image"
              badge ran under the remove button and read as "Leading ima". */}
          {photos.map((photo, index) => (
            <figure
              key={photo.id}
              draggable={canEdit}
              onDragStart={() => setDragging(photo.id)}
              onDragEnd={() => setDragging(null)}
              onDragOver={(event) => event.preventDefault()}
              onDrop={(event) => {
                event.preventDefault();
                if (dragging && dragging !== photo.id) moveTo(dragging, index);
                setDragging(null);
              }}
              className={`group relative overflow-hidden rounded-[var(--radius-surface)] border bg-ink-50 ${
                dragging === photo.id ? "border-brand-500 opacity-50" : "border-ink-200"
              }`}
            >
              <img
                src={developmentPhotoSrc(photo.id)}
                alt={photo.name}
                className={`aspect-[4/3] w-full object-cover ${canEdit ? "cursor-grab active:cursor-grabbing" : ""}`}
                draggable={false}
              />

              {/* The first one is not just first in a list — it is the picture the public card
                  uses. Saying so is what makes the drag feel worth doing. */}
              {index === 0 ? (
                <figcaption className="absolute left-2 top-2 flex items-center gap-1 rounded-[var(--radius-control)] bg-brand-600 px-2 py-0.5 text-[11px] font-semibold text-white">
                  <IconLeading size={12} />
                  Leading image
                </figcaption>
              ) : null}

              {canEdit ? (
                <span className="absolute right-2 top-2 flex gap-1">
                  <button
                    type="button"
                    aria-label={`Remove ${photo.name}`}
                    onClick={() => remove.mutate(photo.id)}
                    disabled={remove.isPending}
                    className="grid size-7 place-items-center rounded-full bg-white/90 text-ink-600 hover:bg-danger-600 hover:text-white"
                  >
                    <IconClose size={14} />
                  </button>
                </span>
              ) : null}

              {/* A drag is not reachable without a mouse; these are. */}
              {canEdit ? (
                <span className="absolute inset-x-2 bottom-2 flex items-center gap-1">
                  <span className="grid size-7 place-items-center rounded-[var(--radius-control)] bg-white/90 text-ink-400">
                    <IconDrag size={14} />
                  </span>
                  <button
                    type="button"
                    aria-label="Move earlier"
                    disabled={index === 0 || reorder.isPending}
                    onClick={() => move(photo.id, -1)}
                    className="grid size-7 place-items-center rounded-[var(--radius-control)] bg-white/90 text-ink-600 disabled:opacity-40 hover:bg-white"
                  >
                    ←
                  </button>
                  <button
                    type="button"
                    aria-label="Move later"
                    disabled={index === photos.length - 1 || reorder.isPending}
                    onClick={() => move(photo.id, 1)}
                    className="grid size-7 place-items-center rounded-[var(--radius-control)] bg-white/90 text-ink-600 disabled:opacity-40 hover:bg-white"
                  >
                    →
                  </button>
                </span>
              ) : null}
            </figure>
          ))}

          {canEdit && photos.length < MAX_GALLERY ? (
            <button
              type="button"
              onClick={() => fileInput.current?.click()}
              disabled={uploading}
              className="flex aspect-[4/3] flex-col items-center justify-center gap-1 rounded-[var(--radius-surface)] border border-dashed border-ink-300 text-sm text-ink-500 hover:border-brand-500 hover:bg-brand-50 hover:text-brand-700"
            >
              <IconPlus size={20} />
              {uploading ? "Uploading…" : "Add photographs"}
            </button>
          ) : null}
        </div>
      )}

      <input
        ref={fileInput}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/avif"
        multiple
        hidden
        onChange={(event) => upload(event.target.files)}
      />

      {canEdit ? (
        <p className="mt-2 text-xs text-ink-500">
          {photos.length
            ? `Drag to reorder. The first is the leading image — it is the one used on the public listing. ${photos.length} of ${MAX_GALLERY}.`
            : `Up to ${MAX_GALLERY}. JPEG, PNG or WebP, 8MB each. The first photograph becomes the leading image for this ${noun}, and is the one a buyer sees on the public listing.`}
        </p>
      ) : null}

      {canEdit && photos.length ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="mt-2"
          onClick={() => fileInput.current?.click()}
          disabled={uploading || photos.length >= MAX_GALLERY}
        >
          <IconPlus size={15} />
          Add more
        </Button>
      ) : null}
    </div>
  );
}
